import React, { useEffect, useState, useCallback } from 'react';
import { Lightbulb, RefreshCw } from 'lucide-react';
import { api } from '../../lib/api';
import { Card, LoadingState, EmptyState, ErrorState } from '../../components/control-center/StateWrappers';
import { Button } from '../../components/ui/button';
import { format } from 'date-fns';

const fmtDate = (iso) => { try { return iso ? format(new Date(iso), 'dd/MM/yyyy HH:mm') : '—'; } catch { return '—'; } };
const val = (o, ...keys) => { for (const k of keys) if (o?.[k] !== undefined && o?.[k] !== null && o?.[k] !== '') return o[k]; return null; };

const Insights = () => {
  const [state, setState] = useState({ loading: true, error: false, insight: null });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: false }));
    try {
      const { data } = await api.get('/control-center/insights');
      setState({ loading: false, error: false, insight: data?.insight ?? null });
    } catch { setState({ loading: false, error: true, insight: null }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Insights</h1>
          <p className="text-slate-500 text-sm mt-1">Último análisis generado por la automatización.</p>
        </div>
        <Button onClick={load} className="bg-white text-slate-800 border border-slate-200 hover:bg-slate-50 gap-2">
          <RefreshCw className={`w-4 h-4 ${state.loading ? 'animate-spin' : ''}`} /> Actualizar
        </Button>
      </div>

      {state.loading && <LoadingState />}
      {state.error && !state.loading && <ErrorState onRetry={load} />}
      {!state.loading && !state.error && !state.insight && (
        <EmptyState title="Todavía no hay suficientes datos para generar insights." icon={Lightbulb} />
      )}
      {!state.loading && !state.error && state.insight && (
        <InsightView insight={state.insight} />
      )}
    </div>
  );
};

const InsightView = ({ insight }) => {
  if (typeof insight === 'string') {
    return (
      <Card className="p-6 md:p-8">
        <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide mb-2">Resumen</p>
        <p className="text-slate-800 whitespace-pre-wrap leading-relaxed">{insight}</p>
      </Card>
    );
  }
  const title = val(insight, 'titulo', 'title', 'headline');
  const summary = val(insight, 'resumen', 'summary', 'text', 'content', 'insight_text');
  const generated = val(insight, 'fecha', 'generated_at', 'created_at');
  const period = val(insight, 'periodo', 'period');
  const emailsCount = val(insight, 'emails_analizados', 'emails_analyzed');
  const highlights = insight.highlights || insight.puntos_clave || insight.key_points;
  const recommendations = insight.recomendaciones || insight.recommendations;

  return (
    <div className="space-y-4">
      <Card className="p-6 md:p-8">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs text-blue-600 font-semibold uppercase tracking-wide">Informe ejecutivo</span>
          <span className="text-xs text-slate-500">{fmtDate(generated)}</span>
        </div>
        {title && <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">{title}</h2>}
        {period && <p className="text-sm text-slate-500 mb-4">Periodo: {period}{emailsCount ? ` · ${emailsCount} correos analizados` : ''}</p>}
        {summary && <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{summary}</p>}
      </Card>

      {Array.isArray(highlights) && highlights.length > 0 && (
        <Card className="p-6 md:p-8">
          <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide mb-4">Puntos clave</p>
          <ul className="space-y-3">
            {highlights.map((h, i) => (
              <li key={i} className="flex gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-600 shrink-0" />
                <span className="text-slate-700">{typeof h === 'string' ? h : JSON.stringify(h)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {Array.isArray(recommendations) && recommendations.length > 0 && (
        <Card className="p-6 md:p-8">
          <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide mb-4">Recomendaciones</p>
          <ul className="space-y-3">
            {recommendations.map((r, i) => (
              <li key={i} className="flex gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-900 shrink-0" />
                <span className="text-slate-700">{typeof r === 'string' ? r : JSON.stringify(r)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
};

export default Insights;
